import type { RequestStatus, GenericError } from '@skynet/data-ws-core';

export interface RetainerTermsState {
  id?: number;
  details?: RetainerTerms;
  requestStatus: RequestStatus;
  resUpdateRetainer: RequestStatus;
  /**
   * The server-confirmed `id` and `details` from before the update in flight merged its payload
   * into `details` — which it does optimistically, before the server acks it. Restored if the
   * update is refused or its ack is lost, so the form falls back to what the server kept rather
   * than the refused values; `undefined` while no update is in flight.
   */
  confirmedTerms?: { id?: number; details?: RetainerTerms };
  error: null | GenericError;
  isRetainerEditing: boolean;
}

export enum RetainerTermsCommands {
  Terms = 'Terms',
  UpdateRetainer = 'UpdateRetainer',
  AddProject = 'AddProject',
}

export interface JsonAddRetainerProjectPayload {
  company: string; // Company name
  name: string; // Loan name
  tokens: string[];
  started: string; // string in YYYY-MM-DD format
  ended: string; // string in YYYY-MM-DD format
  exchanges: number[]; // list of exchanges ids
  pairs: string[]; // array of supported pairs, like TOKEN/USDT, TOKEN/BTC, TOKEN/BUSD
  wallets: string;
  autoSyncPairs: boolean; // when true, pairs/tokens are computed dynamically from active accounts; read-only on the UI
  coinGeckoProject?: string; // CoinGecko project id (e.g. `bitcoin`); the backend derives `coinGeckoLink` from it
}

export interface AddRetainerProjectPayload extends Omit<JsonAddRetainerProjectPayload, 'tokens'> {
  tokens: string[];
}

export interface AdditionalRetainerProjectPayload {
  projectID: number;
  baseAmount?: number;
  quoteAmount?: number;
  basePaybackAmount?: number;
  quotePaybackAmount?: number;
  residualPaybackAmount?: number;
}

export interface JsonUpdateRetainerProjectPayload
  extends Partial<JsonAddRetainerProjectPayload>, AdditionalRetainerProjectPayload {}

export interface JsonRetainerTerms {
  company: string; // Company name
  // CoinGecko project id, the only editable CoinGecko value. Optional on the read side only:
  // projects stored before SKY-4764 come back without the key — treat it as an empty id.
  coinGeckoProject?: string;
  // Read-only and never sent: the backend computes it from `coinGeckoProject`. Always
  // present — an empty string when there is no project to derive it from.
  coinGeckoLink: string;
  name: string; // Loan name
  started: string; // string in YYYY-MM-DD format
  ended: string; // string in YYYY-MM-DD format
  initFunds: number; // used for a new loan form only
  exchanges: number[]; // list of exchanges ids
  pairs: string[]; // array of supported pairs, like TOKEN/USDT, TOKEN/BTC, TOKEN/BUSD
  tokens: string[]; // Token name
  // Optional on the read side only: projects stored before SKY-4553 come back
  // without the key. Treat a missing value as `false`.
  autoSyncPairs?: boolean;
  splitOverview?: boolean;
}

export interface RetainerTerms extends JsonRetainerTerms {
  price?: number;
}

export interface JsonRetainerProjectTermsSuccess {
  command: RetainerTermsCommands.Terms;
  details: JsonRetainerTerms;
  id: number;
}

export interface JsonAddRetainerProjectSuccess {
  command: RetainerTermsCommands.AddProject;
  result: number;
}
